import React, { Dispatch, SetStateAction, useState } from 'react'
import { DataGridPro, gridClasses, GridSlots } from '@mui/x-data-grid-pro'
import { formatDateYYYYMMDD, formatMoney } from './../../../packages/utils/numberFormatters'
import { useAppLocale } from '../../../i18n/localeUtils'
import { Box, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material'
import { CustomDataGridToolbar } from '../../../components/custom-grid-components/CustomDataGridToolbar'
import { McIssueListItem } from '@finance-lab/utils/types'

type MCIssuesGridComponent = React.FC<{
    mcIssues: McIssueListItem[]
    setSelectedMCIssue: Dispatch<SetStateAction<McIssueListItem | null>>
}>

const disableSelectedSellOutlineStyle = {
    [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
        outline: 'none',
    },
    [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
        outline: 'none',
    },
}

function CustomNoResultsOverlay() {
    return (
        <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant='body1'>Inga emissioner hittades</Typography>
        </Box>
    )
}

const columnDefinitions = (locale: string) => [
    { field: 'name', headerName: 'Aktie', minWidth: 90, flex: 1, disableColumnMenu: true },
    {
        field: 'actionDate',
        headerName: 'Datum',
        width: 100,
        disableColumnMenu: true,
        valueFormatter: (value: string) => (value ? formatDateYYYYMMDD(new Date(value), { locale }) : ''),
    },
    {
        field: 'subscriptionPrice',
        headerName: 'Pris',
        width: 80,
        disableColumnMenu: true,
        valueFormatter: (value: string | null) => {
            if (!value) {
                return '-'
            }
            return formatMoney(value, { locale, minDecimals: 2, maxDecimals: 5 })
        },
    },
    /*     {
        field: 'numberOfShares',
        headerName: 'Antal aktier',
        width: 120,
        disableColumnMenu: true,
        valueFormatter: (value: number | null) => {
            if (!value) {
                return '-'
            }
            return formatNumber(value, { locale, decimals: 0 })
        },
    }, */
    {
        field: 'newOld',
        headerName: 'Nya:Gamla',
        width: 100,
        disableColumnMenu: true,
        valueFormatter: (value: undefined, row: McIssueListItem) => {
            if (!row.newShares || !row.oldShares) {
                return ''
            }
            return `${parseInt(row.newShares)}:${parseInt(row.oldShares)}`
        },
    },
    { field: 'actionComment', headerName: 'Kommentar', minWidth: 150, flex: 1, disableColumnMenu: true },
]

export const MCIssuesGrid: MCIssuesGridComponent = ({ mcIssues, setSelectedMCIssue }) => {
    const [showMatchedIssues, setShowMatchedIssues] = useState(false)
    const [tablePage, setTablePage] = useState(0)
    const { language: locale } = useAppLocale()

    return (
        <Stack>
            <Box>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={showMatchedIssues}
                            onChange={() => setShowMatchedIssues((prev) => !prev)}
                            color='primary'
                            disableRipple
                        />
                    }
                    label='Visa avstämda emissioner'
                />
            </Box>
            <DataGridPro
                rows={mcIssues.filter((issue) => (showMatchedIssues ? showMatchedIssues : !issue.matchedTo))}
                getRowId={(row) => `${row.id}-${row.matchedTo}`}
                pagination
                pageSizeOptions={[25]}
                paginationModel={{
                    pageSize: 25,
                    page: tablePage,
                }}
                onPaginationModelChange={({ page }) => {
                    setTablePage(page)
                }}
                hideFooterSelectedRowCount
                sx={disableSelectedSellOutlineStyle}
                columns={columnDefinitions(locale)}
                onRowClick={(params) => {
                    setSelectedMCIssue(params.row)
                }}
                slots={{
                    noResultsOverlay: CustomNoResultsOverlay,
                    toolbar: CustomDataGridToolbar as GridSlots['toolbar'],
                }}
                initialState={{
                    sorting: {
                        sortModel: [
                            { field: 'actionDate', sort: 'desc' },
                            { field: 'name', sort: 'asc' },
                        ],
                    },
                }}
                showToolbar
            />
        </Stack>
    )
}
